import { useCallback, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CopyAction, IconButton, tokens } from '@hotjar/hotsauce';
import styled from '@emotion/styled';

import { tagRecording } from '@hotjar/webapp/tracking/tools/hotjar';
import type { SiteId } from '@hotjar/site';
import { getShortUrl } from '@hotjar/webapp/domains/short-link/api';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { ZINDEX as REACT_MODAL_ZINDEX } from '@hotjar/webapp/components/GenericModal';
import { selectText } from '@hotjar/webapp/utils/select-text';

import { useIsDemoUser } from '@hotjar/webapp/hooks/useIsDemoUser';
import { useAutoCopy } from './use-auto-copy-to-clipboard';
import { useShortLinkWidgetHiddenState } from './ShortLinkWidgetContext';

const SIZE = '40px';
const ID = 'short-link-widget';
const SHORT_LINK_PREFIX = 'Short Link';
const TRANSITION_TIME = 250;
const HOTJAR_DEMO_LINK = `${window.location.origin}/login?demo`;

interface ElementProps {
  isExpanded: boolean;
}

function getSize({ isExpanded }: ElementProps) {
  if (!isExpanded) {
    return SIZE;
  }

  return 'auto';
}

export const getItemFromUrl = (pageUrl: string): string | undefined => {
  if (pageUrl.includes('heatmap')) {
    return `${SHORT_LINK_PREFIX} Heatmaps`;
  }
  if (pageUrl.includes('surveys')) {
    return `${SHORT_LINK_PREFIX} Surveys`;
  }
  if (pageUrl.includes('feedback')) {
    return `${SHORT_LINK_PREFIX} Feedback`;
  }
  if (pageUrl.includes('highlights')) {
    return `${SHORT_LINK_PREFIX} Highlights`;
  }
  if (pageUrl.includes('dashboard')) {
    return `${SHORT_LINK_PREFIX} Dashboard`;
  }
  if (pageUrl.includes('overview')) {
    return `${SHORT_LINK_PREFIX} Overview`;
  }

  return undefined;
};

type WrapperProps = ElementProps & { isHidden?: boolean };

const Wrapper = styled.form<WrapperProps>`
  position: fixed;
  bottom: ${tokens.spacer.x2};
  right: ${tokens.spacer.x2};
  /* NOTE: It's minimal value letting it be visible above IF response overlay */
  z-index: ${REACT_MODAL_ZINDEX + 1};

  display: ${({ isHidden }) => (isHidden ? 'none' : 'flex')};
  flex-wrap: nowrap;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  box-sizing: border-box;
  padding: 0;
  overflow: hidden;
  height: ${SIZE};
  width: ${getSize};
  transition: width ${TRANSITION_TIME}ms ease-in-out;

  background-color: ${tokens.color.ui_background};
  border-radius: ${SIZE};
  ${tokens.elevation(2)};
`;

const RoundedButton = styled(IconButton)`
  button {
    border-radius: 50%;
    margin-left: ${tokens.spacer.x0};

    &:hover,
    &:active,
    &:focus {
      border-radius: 50%;
      ${tokens.elevation(1)}
    }
  }
`;

const UrlOutput = styled.output<ElementProps>`
  flex-grow: 1;
  margin-left: ${tokens.spacer.x2};
  margin-right: ${tokens.spacer.x1};

  padding: 0;

  border: none;
  ${tokens.text('caption')}
  overflow: hidden;
  white-space: nowrap;
  opacity: ${({ isExpanded }) => (isExpanded ? 1 : 0)};
  transition: opacity ${TRANSITION_TIME}ms ease-in-out;
  transition-delay: ${({ isExpanded }) => (isExpanded ? TRANSITION_TIME : 0)}ms;
`;

export interface ShortLinkWidgetProps {
  siteId: SiteId | null | undefined;
  initialIsExpanded?: boolean;
  initialUrl?: string;
}

export const ShortLinkWidget = ({
  siteId,
  initialIsExpanded = false,
  initialUrl,
}: ShortLinkWidgetProps): JSX.Element | null => {
  const [isExpanded, setIsExpanded] = useState(initialIsExpanded);
  const [shortUrl, setShortUrl] = useState(initialUrl);
  const { itemShared } = useTrack();
  const isDemoUser = useIsDemoUser();
  const { isHidden } = useShortLinkWidgetHiddenState();

  // FIXME: when app will be moved to the React remember to use useLocation hook;
  // Temporarily changed due to the react-router problems with taking params for URL
  const fullPageUrl = window.location.href;

  const fold = useCallback(() => {
    setIsExpanded(false);
  }, [setIsExpanded]);
  const toggle = () => {
    if (isExpanded) {
      fold();
    } else {
      setIsExpanded(true);
    }
  };
  const outputRef = useRef<HTMLOutputElement | null>(null);
  const selectAndCopy = useCallback(() => {
    if (outputRef.current !== null && typeof shortUrl !== 'undefined') {
      selectText(outputRef.current);

      if (isExpanded) {
        itemShared({ item: getItemFromUrl(fullPageUrl) });
      }
      tagRecording('share_toggle');
    }
  }, [shortUrl, fullPageUrl, itemShared, isExpanded]);

  const { t } = useTranslation();

  useAutoCopy({ shortUrl, selectAndCopy, transitionTime: TRANSITION_TIME });

  useEffect(() => {
    if (!isExpanded || siteId === null || siteId === undefined) {
      return;
    }

    if (isDemoUser) {
      setShortUrl(HOTJAR_DEMO_LINK);
    } else {
      getShortUrl(String(siteId), fullPageUrl).then(setShortUrl);
    }

    // TODO: on effect cleanup - abort the request somehow
  }, [isExpanded, shortUrl, fullPageUrl, siteId, setShortUrl, isDemoUser]);

  const handleAnyClick = useCallback(
    (e: MouseEvent) => {
      if (e.target === null || !(e.target instanceof Element) || e.target.closest(`#${ID}`)) {
        return;
      }

      fold();
    },
    [fold],
  );

  useEffect(() => {
    document.addEventListener('click', handleAnyClick, true);

    return () => document.removeEventListener('click', handleAnyClick, true);
  }, [handleAnyClick]);

  if (siteId === null || siteId === undefined) {
    return null;
  }

  return (
    <Wrapper isExpanded={isExpanded} id={ID} isHidden={isHidden}>
      <UrlOutput isExpanded={isExpanded} aria-label={t('common:short-link.link')} ref={outputRef}>
        {shortUrl ?? t('common:short-link.generating')}
      </UrlOutput>
      <CopyAction
        toCopy={shortUrl as string}
        onCopy={selectAndCopy}
        description={t('common:short-link.copy')}
        actionFeedback={t('common:short-link.success-message')}
      />
      <RoundedButton
        icon={isExpanded ? 'close' : 'hyperlink'}
        tooltipText={isExpanded ? t('common:short-link.close') : t('common:short-link.tooltip')}
        onClick={toggle}
        kind="ghost"
      />
    </Wrapper>
  );
};
